import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Subject } from 'rxjs/Subject';
import { Http } from '@angular/http';
import moment from 'moment-es6';
import 'rxjs/add/observable/of';
import { of } from 'rxjs';
import { BootstrapService } from '@kaiser-software/chassis-core/src/app/bootstrap/bootstrap.service';


@Injectable()
export class TranslationImportService {

    /**
     * Indicates if all mandatory data has been loaded.
     *
     * @type {ReplaySubject<any>}
     */
    private ready: Subject<boolean> = new BehaviorSubject(false);

    constructor(private http: Http, private translate: TranslateService, bootstrap: BootstrapService) {
        bootstrap.addToSequence(() => {
            return new Observable((observer) => {
                this.ready.subscribe((value) => {
                    if (value === true) {
                        observer.next();
                    }
                });
            });
        });
    }

    /**
     * Set the translations of the given language.
     *
     * @param {string} endpointUrl
     * @param {string} languageCode
     */
    public setTranslations(endpointUrl: string, languageCode: string) {
        this.determineNewImport(endpointUrl).subscribe(shouldBeImported => {
            if (shouldBeImported === true) {
                this.importTranslations(endpointUrl, languageCode);
            } else {
                const translations = JSON.parse(localStorage.getItem('translations-' + this.translate.currentLang));

                this.translate.setTranslation(this.translate.currentLang, translations);

                this.ready.next(true);
            }
        });
    }

    /**
     * Get the translations for the given language.
     *
     * @returns Observable<Object>
     */
    public getTranslations(): Observable<Object> {
        return new Observable(observer => {
            // const translations = JSON.parse(localStorage.getItem('translations'));
            let translations;
            this.translate.getTranslation(this.translate.currentLang).subscribe(result => {
                translations = result;
                console.warn(translations);
            }, error => {
                console.error(error);
            }, () => {
                observer.next(translations);
            });
        });
    }

    /**
     * Determine if the translations should be fetched from the backend
     *
     * @return {Promise<boolean>} 'true' if translation should be imported, 'false' if not.
     * @author Thim Heider <thim@kaiserinternet.nl>
     * @author Kevin Broeren <kevin@kaiserinternet.nl>
     */
    private determineNewImport(endpointUrl): Observable<boolean> {
        const TRANSLATION_FILE                          = localStorage.getItem('translations-' + this.translate.currentLang);
        const TRANSLATION_FILE_LAST_CHECKED_FOR_UPDATES = localStorage.getItem('translations-' + this.translate.currentLang + '-last_checked_for_updates');
        const TRANSLATION_FILE_LAST_MODIFIED            = localStorage.getItem('translations-' + this.translate.currentLang + '-last_modified');
        const MILISECONDS_IN_TWO_HOURS                  = 7200000;

        // If translation file exists.
        if (TRANSLATION_FILE) {

            // If we know when we last checked for updates on the current language file
            // and we know when that file has last been modified.
            if (TRANSLATION_FILE_LAST_CHECKED_FOR_UPDATES && TRANSLATION_FILE_LAST_MODIFIED) {

                // If we last checked for updates more than 2 hours ago. We will check for
                // updates on the server, by executing a head request to the server and
                // comparing the local 'last modified' information with the server's 'last
                // modified' information from the response headers.
                if (moment().diff(moment(new Date(TRANSLATION_FILE_LAST_CHECKED_FOR_UPDATES))) > MILISECONDS_IN_TWO_HOURS) {

                    return new Observable(observer => {

                        this.http.head(endpointUrl).subscribe((response) => {
                            localStorage.setItem('translations-' + this.translate.currentLang + '-last_checked_for_updates', (new Date()).toString());
                            const last_modified_server = moment(new Date(response.headers['_headers'].get('last-modified')));
                            const last_modified_local  = moment(new Date(TRANSLATION_FILE_LAST_MODIFIED));

                            // True? The file on the server contains updates to our local file. False? No
                            // updates available.
                            observer.next(last_modified_server.diff(last_modified_local) > 0);
                        });

                    });
                }
                // We just checked for updates (within the last 2 hours).
                //     return false, won't import new translations.
                return of(false);
            }
            // No info available about when we last checked for updates or when the
            // translations have been updated the last time.
            //     return true, let's import (new) translations.
            return of(true);
        }
        // Requested translation file does not exist.
        //     return true, let's import translations.
        return of(true);
    }

    /**
     * Retrieves the translations from the backend and try to save them in the Local Storage.
     *      Catch any errors and send them to the console (recommended by Storage.setItem() documentation: https://developer.mozilla.org/en-US/docs/Web/API/Storage/setItem).
     *
     * @param endpointUrl
     * @param languageCode
     * @param observer
     */
    private importTranslations(endpointUrl: string, languageCode: string) {
        /**
         * Fetch the translations from the backend, when done, call the saveTranslations method.
         */
        let translations;
        this.http.get(endpointUrl).subscribe((response) => {
            translations = response.json();
            localStorage.setItem('translations-' + this.translate.currentLang + '-last_checked_for_updates', (new Date()).toString());
            localStorage.setItem('translations-' + this.translate.currentLang + '-last_modified', response.headers['_headers'].get('last-modified'));
        }, (error) => {
            console.error(error);
        }, () => {
            this.saveTranslations(languageCode, translations);

            this.ready.next(true);
        });
    }

    /**
     * Call the parseTranslations method to convert the received key-value pairs (data) to Angular compatible format.
     * Save the parsed translations to a translation file, using the TranslateService.
     * Try to save the parsed translations to the Local Storage.
     *      Catch any errors and send them to the console (recommended by Storage.setItem() documentation: https://developer.mozilla.org/en-US/docs/Web/API/Storage/setItem).
     *
     * @param languageCode
     * @param data
     */
    private saveTranslations(languageCode: string, data: Object) {
        const translations = this.parseTranslations(data);
        this.translate.setTranslation(languageCode, translations, true);
        try {
            localStorage.setItem('translations-' + this.translate.currentLang, JSON.stringify(translations));
        } catch (error) {
            console.error(error);
        }
    }


    /**
     * Convert the received key-value pairs from Laravel (data) to Angular compatible format.
     *
     * @param data
     * @returns Object
     */
    private parseTranslations(data: Object): Object {
        const regex              = /:(\w[^\s]*)\s?.?/ig; // The regex to select all Laravel parameters from a translation string; Laravel parameter pattern:  ( ':variable ' or ':variable.' ).
        const keys               = Object.keys(data); // Get the keys convert replace variable format from Laravel to Angular variable format.
        const newKeys            = []; // Temporary new keys array, to store the converted keys.
        const newValues          = []; // Temporary new values array, to store the converted values.
        const parsedTranslations = {}; // Object to store the converted key-value pairs.

        /**
         * For every key, convert the key and its value to Angular compatible format.
         */
        for (const key of keys) {
            /**
             * Execute the parsing on the keys
             */
            const keyMatches = key.match(regex); // Get all words from the key that match the pattern.
            const parsedKey  = this.executeParse(key, keyMatches); // Gets the new translation string (if no conversion executed, gets the original translation string).
            newKeys.push(parsedKey); // Add the received translation string (from the executeParse method) to the newKeys[].

            /**
             * Execute the parsing on the values
             */
            const valueMatches = data[key].match(regex); // Get all words from the key its value that match the pattern.
            const parsedValue  = this.executeParse(data[key], valueMatches); // Gets the new translation string (if no conversion executed, gets the original translation string).
            newValues.push(parsedValue); // Add the received translation string (from the executeParse method) to the newValues[].
        }

        /**
         * Add the converted key-value pairs to the parsedTranslations object and return parsedTranslations.
         *
         * Using newKeys array length for looping, because there cannot be a value without key.
         */
        for (let i = 0; i < newKeys.length; i++) {
            parsedTranslations[newKeys[i]] = newValues[i];
        }
        return parsedTranslations;
    }

    /**
     * Convert the matches in the originalItem to Angular compatible and return the converted item.
     * If there are no matches, return the originalItem.
     *
     * @param originalItem
     * @param matches
     * @returns string
     */
    private executeParse(originalItem: string, matches: Array<string>): string {
        /**
         * If there are any words in the matches[]:
         *      replace ':' for '{{'
         *      replace ' ' for '}} ' (if present)
         *      replace '.' for '}}.' (if present)
         */
        if (matches) {
            let newItem = originalItem;
            for (const match of matches) {
                let newMatch = match;
                newMatch     = match.replace(':', '{{');
                newMatch     = newMatch.replace(' ', '}} ');
                newMatch     = newMatch.replace('.', '}}.');
                newItem      = newItem.replace(match, newMatch);
            }
            return newItem;
        } else {
            return originalItem;
        }
    }

}



// WEBPACK FOOTER //
// ./libs/chassis-localization/src/services/translation-import.service.ts